import { Box, Button, Flex, IconButton, Kbd, Menu, Portal, Spacer, Text } from "@chakra-ui/react";
import { isDesktop } from 'react-device-detect';
import Icon, { Icons } from "./Icons";
import { forwardRef, useContext, useState } from "react";
import { Context } from "../App";
import { useNavigate } from "react-router-dom";
import { RoutePath, SPACING } from "../generic/config";
import { useAuth } from "react-oidc-context";
import { setSelectedInstanceId } from "../generic/authStore";
import useToast from "./hooks/useToast";
import Tooltip from "./Tooltip";
import Drawer from "@/generic/Drawer";
import { PopoverCloseTrigger, PopoverContent, PopoverRoot, PopoverTrigger } from "@/components/ui/popover";
import usePalette from "./hooks/usePalette";
import { QueryKeys } from "@/api/base/react-query";
import { GetInstances } from "@/api/authentication-controller";
import { useQuery } from "react-query";
import { useColorMode } from "@/components/ui/color-mode";
import { Language } from "@/generic/Translations";
import { isMac, openCommandPalette } from "./CommandPalette";

/** Display name from the sign-in token: full name, else the part of the username before "@". */
export const useDisplayName = () => {
    const auth = useAuth();
    const { username } = useContext(Context);
    const profile = auth.user?.profile;
    const full = [profile?.given_name, profile?.family_name].filter(Boolean).join(" ") || (profile?.name as string | undefined) || "";
    const fallback = (username ?? "").split("@")[0];
    return {
        full: full || fallback,
        first: (profile?.given_name as string | undefined) || full.split(" ")[0] || (fallback ? fallback.charAt(0).toUpperCase() + fallback.slice(1) : ""),
        email: (profile?.email as string | undefined) || username || "",
    };
};

const initials = (name: string) => name.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map(p => p[0]!.toUpperCase()).join("") || "?";

const BrandMark = () => (
    <Flex w={7} h={7} borderRadius="md" bg="fg" color="bg" alignItems="center" justifyContent="center"
        fontWeight="800" fontSize="sm" letterSpacing="-0.04em" flexShrink={0} aria-hidden>
        F
    </Flex>
);

const Header = forwardRef<HTMLDivElement, {}>((props, ref) => {
    const { background } = usePalette();
    const { username, translations, companySettings, language, setLanguage } = useContext(Context);
    const navigate = useNavigate();
    const auth = useAuth();
    const toast = useToast();
    const { colorMode, toggleColorMode } = useColorMode();
    const instances = useQuery(QueryKeys.instances, GetInstances, { enabled: !!username });
    const hasMultipleInstances = (instances.data?.length ?? 0) > 1;
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const companyName = username ? companySettings?.data?.name ?? "" : "";
    const onSelectInstance = window.location.href.includes(RoutePath.SelectInstance);

    const signOut = () => {
        setSelectedInstanceId(null);
        void auth.signoutRedirect();
        toast(translations.signedOut, "", "success");
    };

    const themeButton = (
        <Tooltip tooltip={translations.switchTheme}>
            <IconButton size="sm" variant="ghost" aria-label={translations.switchTheme} onClick={toggleColorMode}>
                <Icon icon={colorMode === "dark" ? Icons.Sun : Icons.Moon} />
            </IconButton>
        </Tooltip>
    );
    const languageButton = (
        <Tooltip tooltip={translations.switchLanguage}>
            <Button size="sm" variant="ghost" px={2} fontWeight="600" fontSize="xs" aria-label={translations.switchLanguage}
                onClick={() => setLanguage(language === Language.EN ? Language.SE : Language.EN)}>
                {language === Language.EN ? "EN" : "SV"}
            </Button>
        </Tooltip>
    );

    const company = companyName && (
        hasMultipleInstances && !onSelectInstance ? (
            <Tooltip tooltip={translations.header.switchInstallation}>
                <Button size="sm" variant="outline" fontWeight="500" onClick={() => navigate(RoutePath.SelectInstance)} data-testid="header-company">
                    <Icon icon={Icons.company} /> {companyName} <Icon icon={Icons.caretDown} />
                </Button>
            </Tooltip>
        ) : (
            <Tooltip tooltip={translations.installation.currentInstallation}>
                <Flex alignItems="center" gap={SPACING} px={SPACING} h={8} borderWidth="1px" borderRadius="md" fontSize="sm" fontWeight="500" data-testid="header-company">
                    <Box color="fg.muted"><Icon icon={Icons.company} /></Box>{companyName}
                </Flex>
            </Tooltip>
        )
    );

    const account = username && <AccountMenu onSignOut={signOut} />;

    const bar = (children: React.ReactNode) => (
        <Box ref={ref} as="header" position="fixed" left={0} right={0} top={0} zIndex={99}
            bg="bg.panel" borderBottomWidth="1px" data-testid="app-header">
            <Flex h={14} alignItems="center" gap={SPACING} px={SPACING * 2}>{children}</Flex>
        </Box>
    );

    if (isDesktop) {
        return bar(<>
            <Flex alignItems="center" gap={SPACING * 1.5} w="15rem" flexShrink={0} cursor="pointer" onClick={() => navigate(RoutePath.Dashboard)}>
                <BrandMark />
                <Box lineHeight="1.1" textAlign="start">
                    <Text fontWeight="700" fontSize="sm" letterSpacing="-0.01em">Code F</Text>
                    <Text fontSize="xs" color="fg.muted">{translations.header.product}</Text>
                </Box>
            </Flex>
            {username && (
                <Button variant="outline" size="sm" w="22rem" justifyContent="flex-start" color="fg.muted" fontWeight="400"
                    onClick={openCommandPalette} data-testid="header-search">
                    <Icon icon={Icons.Search} />
                    <Text flex={1} textAlign="start" fontSize="sm">{translations.commandPalette.placeholder}</Text>
                    <Kbd size="sm">{isMac() ? "⌘" : "Ctrl"}</Kbd><Kbd size="sm">K</Kbd>
                </Button>
            )}
            <Spacer />
            {company}
            {themeButton}
            {languageButton}
            {account}
        </>);
    }

    return bar(<>
        <PopoverRoot open={isDrawerOpen} onOpenChange={e => setIsDrawerOpen(e.open)}>
            <PopoverTrigger asChild>
                <IconButton size="sm" variant="ghost" aria-label="Menu"><Icon icon={Icons.menu} /></IconButton>
            </PopoverTrigger>
            <PopoverContent shadow="none">
                <Box bg={background.lightest} minH={"100vh"} minW="101vw" ml={-SPACING} mt={-SPACING * 6} overflow="hidden">
                    <Drawer ratioInPercent={50} onSelect={() => setIsDrawerOpen(false)} />
                </Box>
                <Box position="fixed" ml={-SPACING * 2} mt={-SPACING * 6} minH={"3ch"} minW="99vw" zIndex={100}>
                    <PopoverCloseTrigger />
                </Box>
            </PopoverContent>
        </PopoverRoot>
        <BrandMark />
        <Box lineHeight="1.1" textAlign="start" minW={0}>
            <Text fontWeight="700" fontSize="sm">Code F</Text>
            {companyName && <Text fontSize="xs" color="fg.muted" truncate>{companyName}</Text>}
        </Box>
        <Spacer />
        {hasMultipleInstances && !onSelectInstance && (
            <IconButton size="sm" variant="ghost" aria-label={translations.header.switchInstallation} onClick={() => navigate(RoutePath.SelectInstance)}>
                <Icon icon={Icons.company} />
            </IconButton>
        )}
        {username && (
            <IconButton size="sm" variant="ghost" aria-label={translations.commandPalette.search} onClick={openCommandPalette}>
                <Icon icon={Icons.Search} />
            </IconButton>
        )}
        {account}
    </>);
});

/** Avatar button with name, email, theme, language and sign out. */
const AccountMenu = ({ onSignOut }: { onSignOut: () => void }) => {
    const { translations, language, setLanguage } = useContext(Context);
    const { colorMode, toggleColorMode } = useColorMode();
    const name = useDisplayName();

    return (
        <Menu.Root positioning={{ placement: "bottom-end" }}>
            <Menu.Trigger asChild>
                <Button variant="ghost" size="sm" px={1} aria-label={translations.header.account} data-testid="header-account">
                    <Flex w={8} h={8} borderRadius="full" bg="bg.emphasized" alignItems="center" justifyContent="center"
                        fontSize="xs" fontWeight="700">
                        {initials(name.full)}
                    </Flex>
                    {isDesktop && <Text fontSize="sm" fontWeight="500" maxW="12rem" truncate>{name.full}</Text>}
                    {isDesktop && <Box color="fg.muted" fontSize="xs"><Icon icon={Icons.caretDown} /></Box>}
                </Button>
            </Menu.Trigger>
            <Portal>
                <Menu.Positioner>
                    <Menu.Content minW="16rem">
                        <Box px={3} py={2} textAlign="start">
                            <Text fontWeight="600" fontSize="sm">{name.full}</Text>
                            <Text fontSize="xs" color="fg.muted" truncate>{name.email}</Text>
                        </Box>
                        <Menu.Separator />
                        {!isDesktop && (
                            <>
                                <Menu.Item value="theme" onClick={toggleColorMode}>
                                    <Icon icon={colorMode === "dark" ? Icons.Sun : Icons.Moon} /> {translations.switchTheme}
                                </Menu.Item>
                                <Menu.Item value="language" onClick={() => setLanguage(language === Language.EN ? Language.SE : Language.EN)}>
                                    <Icon icon={Icons.Language} /> {translations.switchLanguage}
                                </Menu.Item>
                                <Menu.Separator />
                            </>
                        )}
                        <Menu.Item value="signout" onClick={onSignOut} data-testid="header-signout">
                            <Icon icon={Icons.Logout} /> {translations.signOut}
                        </Menu.Item>
                    </Menu.Content>
                </Menu.Positioner>
            </Portal>
        </Menu.Root>
    );
};

export default Header;
